import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Add from '@mui/icons-material/Add'

import { IpMappingRead, IpMappingType, ListIpMappingSortableField } from 'common/api/v1/types'
import { Link } from '../common/Link'
import Table, { TableConfig } from '../common/Table'
import { MissingContent } from '../common/MissingContent'
import Wrapper from '../common/Wrapper'
import { useAddressMappingSelector, useConfirmationDialog, usePageParamsFilteredSelector } from '../../utils'
import Toolbar from '@mui/material/Toolbar'
import {
  ListAddressMappingFilterKey,
  makeListAddressMappingsFilter,
  mapAddressMappingFilterToUrlParam,
  mapUrlParamToAddressMappingFilter,
} from './listAddressMappingFilter'

import { AppDispatch, useRoutes } from '../../store'
import { UrlParamFilteredSearchBar } from '../common/Filters/FilterView/FilteredSearchBar'
import { ActionMenu } from '../common/Table/ActionMenu'
import SettingsIcon from '@mui/icons-material/Settings'
import DeleteIcon from '@mui/icons-material/Delete'
import { useDispatch } from 'react-redux'
import { removeIpMapping, writeModelFromReadModel } from '../../redux/actions/addressMappingActions'

export const ListView = () => {
  const routes = useRoutes()
  const { ipMappings, loading } = usePageParamsFilteredSelector(useAddressMappingSelector)
  const showConfirmation = useConfirmationDialog()
  const dispatch = useDispatch<AppDispatch>()

  const tableConfig: TableConfig<IpMappingRead, ListIpMappingSortableField> = [
    {
      title: 'region',
      getValue: ({ region }) =>
        (region && (
          <Link to={routes.regionsUpdate({ id: region.id })} underline="hover">
            <Typography variant="body2" color="textSecondary">
              {region.name}
            </Typography>
          </Link>
        )) ||
        null,
      sorting: {
        byParameter: ListIpMappingSortableField.regionName,
      },
    },
    {
      title: 'appliance',
      getValue: ({ nics }) =>
        nics.map((n) => (
          <Link key={n.appliance.id} to={routes.appliancesUpdate({ id: n.appliance.id })} underline="hover">
            <Typography variant="body2" color="textSecondary">
              {n.appliance.name}
            </Typography>
          </Link>
        )) || null,
      sorting: {
        byParameter: ListIpMappingSortableField.applianceName,
      },
    },
    {
      title: 'interface',
      getValue: (mapping) =>
        mapping.nics.map((n) => (
          <Typography key={n.id} variant="body2" color="textSecondary">
            {n.name}
          </Typography>
        )) || null,
      sorting: {
        byParameter: ListIpMappingSortableField.interfaceName,
      },
    },
    {
      title: 'private address',
      getValue: ({ privateIp, type }) =>
        (privateIp && (
          <Typography color={type === IpMappingType.suggested ? 'textSecondary' : 'textPrimary'}>
            {privateIp}
          </Typography>
        )) ||
        null,
      sorting: {
        byParameter: ListIpMappingSortableField.privateAddress,
      },
    },
    {
      title: 'public address',
      getValue: ({ publicIp }) => publicIp,
      sorting: {
        byParameter: ListIpMappingSortableField.publicAddress,
      },
    },
    {
      title: 'internal address',
      getValue: ({ internalIp }) => internalIp,
      sorting: {
        byParameter: ListIpMappingSortableField.internalAddress,
      },
    },
    {
      title: 'inter region address',
      getValue: ({ interRegionIp }) => interRegionIp,
      sorting: {
        byParameter: ListIpMappingSortableField.interRegionAddress,
      },
    },
    {
      getValue: (mapping) =>
        mapping.type === IpMappingType.suggested ? (
          <Link
            to={routes.addressMappingsUpdate({ privateIp: mapping.privateIp, region: mapping.region.id })}
            tabIndex={-1}
          >
            <IconButton data-test-id="edit-mapping">
              <Add />
            </IconButton>
          </Link>
        ) : (
          <ActionMenu
            options={[
              {
                icon: <SettingsIcon />,
                label: 'Edit',
                href: routes.addressMappingsUpdate({ region: mapping.region?.id, privateIp: mapping.privateIp }),
                openInNewTab: false,
              },
              {
                icon: <DeleteIcon />,
                label: 'Remove',
                isDestructive: true,
                onClick: () => {
                  showConfirmation(
                    () => dispatch(removeIpMapping(writeModelFromReadModel(mapping))),
                    `This action could potentially disrupt related services that are currently active. Are you sure you want to proceed?`,
                  )
                },
              },
            ]}
          />
        ),
      props: { align: 'right', noTypography: true },
    },
  ]

  const filters = makeListAddressMappingsFilter()
  return (
    <Wrapper
      name="Address mappings"
      actions={
        <Link key="addMapping" id="mappings-table-create-button" to={routes.addressMappingsNew()}>
          <Button variant="contained" color="primary">
            Add mapping
          </Button>
        </Link>
      }
    >
      <Toolbar disableGutters>
        <div style={{ flexShrink: 1, flexGrow: 1 }}>
          <UrlParamFilteredSearchBar
            urlParamCacheKey={'addressMappings'}
            mapUrlParamToFilterFn={mapUrlParamToAddressMappingFilter}
            mapFilterToUrlParamFn={mapAddressMappingFilterToUrlParam}
            searchTokenFilters={filters}
            rawTextFilter={filters.find((f) => f.key === ListAddressMappingFilterKey.search)!}
          />
        </div>
      </Toolbar>
      <Table<IpMappingRead>
        id="mappings"
        emptyMessageComponent={<MissingContent message="No addresses to map" />}
        config={tableConfig}
        data={ipMappings}
        pending={loading}
      />
    </Wrapper>
  )
}
